// apps/web/src/main.tsx
// SPA entry. ReactDOM.createRoot + RouterProvider + QueryClientProvider
// per Plan §4.6.1. Imports global theme CSS at :root.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';

import { router }        from './router/routes.js';
import { ApiError }      from './data/api-client.js';
import { ErrorBoundary } from './primitives/ErrorBoundary.js';
import './theme/index.css';
// §K.17 (2026-05-26) — parallel theme. The :root tokens defined in index.css
// stay the default; v2-dense.css adds a `body[data-theme="v2-dense"]`
// selector that overrides + extends the default tokens. App.tsx sets the
// data-theme attr per route (brief + signal-tracker → v2-dense; everything
// else → default). Loaded eagerly so the cascade is set before the first
// paint.
import './theme/v2-dense.css';

// Step 24.A Q2 lock — global 401 handler routes mid-session expirations
// back to /sign-in across all 22 hooks via a single QueryCache onError.
// requireAuth route-guard still handles INITIAL load (beforeLoad probe);
// this handler covers session expiry that fires AFTER a hook is mounted.
// Skip redirect if already on /sign-in to avoid loops.
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof ApiError && error.status === 401) {
        if (window.location.pathname !== '/sign-in') {
          router.navigate({ to: '/sign-in' }).catch(() => {
            // Router navigate can reject if the destination guards/redirects
            // again; surface the original error path via location as last
            // resort to keep the user out of an authenticated route in an
            // un-authed state.
            window.location.assign('/sign-in');
          });
        }
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry:                1,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('apps/web: #root element not found in index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
